/* eslint-disable @typescript-eslint/no-explicit-any */
import { Textarea } from "@chakra-ui/react";
import DateField from "./DateInputField";
import DateTimeField from "./DateTimeInputField";
import TimeField from "./TimeInputField";
import FileInputField from "./FileInputField";
import OTPField from "./OTPField";
import SwitchField from "./SwitchField";

import { BaseInputProps, FormFieldProps, InputTypes } from "./FormField";
import FormSelectField from "./FormSelectField";
import FormTextEditor from "./FormTextEditor";
import PasswordInputField from "./PasswordInputField";
import InputField from "./InputField";
import { Size } from "chakra-react-select";
import RadioField from "./RadioField";
import EncryptedInputField from "./EncryptedInputField";
import SecretInputField from "./SecretInputField";

export const getFormField = (
  type: InputTypes,
  props: FormFieldProps,
  baseProps: BaseInputProps,
) => {
  const {
    options,
    loadOptions,
    loadingMessage,
    fieldLength,
    removeFieldNameFromQueryParam,
    selectType,
    fieldValuesToIncludeInQueryParam,
    optionalFieldValuesToIncludeInQueryParam,
    label,
    isLoading,
    isCreatable,
    leftAddOn,
    rightAddOn,
    secret,
    ...restProps
  } = props;

  const {
    onInputChange,
    isMulti,
    performAsyncUpload,
    dateFormat,
    ...restbaseProps
  } = baseProps;

  switch (type.toLowerCase()) {
    case "select":
      return (
        <FormSelectField
          {...restbaseProps}
          {...restProps}
          size={(restbaseProps.size as Size) ?? "md"}
          onInputChange={onInputChange}
          isMulti={isMulti}
          isCreatable={isCreatable}
          {...{
            options: options ?? [],
            loadingMessage: loadingMessage,
            loadOptions,
            selectType,
            removeFieldNameFromQueryParam,
            fieldValuesToIncludeInQueryParam,
            optionalFieldValuesToIncludeInQueryParam,
            isLoading,
          }}
        />
      );
    case "textarea":
      return <Textarea {...restbaseProps} {...(restProps as any)} />;
    case "radio":
      return (
        <RadioField
          {...{
            options: (options as { label: string; value: string }[]) ?? [],
            ...restbaseProps,
          }}
        />
      );
    case "file": {
      const {
        maxFiles,
        isMultiFile,
        fileType,
        variant,
        dropzoneHeading,
        dropzoneSubTitle,
        fluid,
        isDropzoneAlwaysVisible,
        maxSize,
      } = props;
      return (
        <FileInputField
          {...baseProps}
          {...{
            performAsyncUpload,
            maxFiles,
            isMultiFile,
            fileType,
            variant,
            label,
            dropzoneHeading,
            dropzoneSubTitle,
            fluid,
            isDropzoneAlwaysVisible,
            maxSize,
          }}
        />
      );
    }
    case "switch":
      return <SwitchField label={label ?? ""} {...restbaseProps} />;
    case "otpfield":
      return (
        <OTPField
          fieldLength={fieldLength ?? 4}
          {...restbaseProps}
          {...restProps}
        />
      );
    case "date": {
      return (
        <DateField
          label={label ?? "DateField"}
          dateFormat={dateFormat}
          disabled={restbaseProps?.isDisabled || false}
          {...{
            ...restbaseProps,
            value: undefined,
            placeholderText: restbaseProps.placeholder,
          }}
        />
      );
    }
    case "datetime-local": {
      const { min, max, allowTyping } = props;
      return (
        <DateTimeField
          label={label ?? "DateTimeField"}
          disabled={restbaseProps?.isDisabled || false}
          min={min !== undefined ? String(min) : undefined}
          max={max !== undefined ? String(max) : undefined}
          allowTyping={allowTyping}
          {...{
            ...restbaseProps,
            value: undefined,
            placeholderText: restbaseProps.placeholder,
          }}
        />
      );
    }
    case "time": {
      return (
        <TimeField
          label={label ?? "TimeField"}
          disabled={restbaseProps?.isDisabled || false}
          {...{
            ...restbaseProps,
            value: undefined,
            placeholderText: restbaseProps.placeholder,
          }}
        />
      );
    }
    case "texteditor": {
      const { name, placeholder } = restbaseProps;
      const { toolbarOptions, style } = props;
      return (
        <FormTextEditor
          {...{
            name,
            placeholder,
            toolbarOptions,
            style,
          }}
        />
      );
    }
    case "password":
      return <PasswordInputField {...restProps} {...restbaseProps} />;
    case "encrypted":
      return <EncryptedInputField {...restProps} {...restbaseProps} />;
    case "secret": {
      // Write-only: Formik's value (`restbaseProps.value`) is never shown.
      const { name, id, placeholder, isDisabled, onBlur } = restbaseProps;
      return (
        <SecretInputField
          {...{ name, id, placeholder, isDisabled, onBlur, secret }}
        />
      );
    }
    default:
      return (
        <InputField
          {...restbaseProps}
          {...restProps}
          leftAddOn={leftAddOn}
          rightAddOn={rightAddOn}
        />
      );
  }
};
